<template>
  <div class="wt-container">
    <p class="introduction-text text-center">
      The locations of your business, {{ scenario.name }}, and their {{ (viewColor == "blue") ? "blue water reduction targets" : "water pollution levels" }}.
    </p>
    <div class="mb-3">
      <label>Visible Columns</label>
      <div class="d-flex">
        <div v-for="(per, i) of ['business location', 'river basin']" :key="'per' + i" class="me-3">
          <template v-for="(columnGroup, j) of columnGroups">
            <template v-if="!columnGroup.color || (columnGroup.color == viewColor)">
              <div v-if="columnGroup.per == per" :key="'columnGroup' + j" class="form-group form-check">
                <input type="checkbox" :disabled="columnGroup.disabled" v-model="columnGroup.visible" :id="'checkbox' + j" class="form-check-input"/>
                <label :for="'checkbox' + j" class="form-check-label">{{ columnGroup.label.replace(hasCsv ? "base" : "_NON_EXIST_", "imported") }} <small class="text-muted"> - per {{columnGroup.per}}</small></label>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
    <Table :columns="columns" :rows="rows" :unparseConfig="unparseConfig" fileName="sbtn-water-targets"></Table>
    <transition name="fade">
      <div v-if="loading" class="lottie">
        <Lottie></Lottie>
      </div>
    </transition>
  </div>
</template>

<script>
import Papa from "papaparse";
import Lottie from "@/components/shared/Lottie.vue";
import Table from "@/components/shared/Table.vue";
import { getFunctionGetWFString, getWFString, getWSString } from "@/services/data-tools.js";
import { months, quantityUnits, scenarioProcesses, scenarioProcessesLoader, scenariosLoader } from "@/services/entity-model.js";

import { assessment } from "@/services/assessment-model.js";

export default {
  name: "ProcessSustainTableCt",
  components: {
    Lottie,
    Table
  },
  data() {
    return {
      scenarioProcesses: scenarioProcesses,
      assessment: assessment,
      columnGroups: [
        {               disabled: true,  visible: true,  label: "base columns", per: "business location"},
        {color: "blue", disabled: true,  visible: true,  label: "annual reduction targets", per: "river basin"},
        {color: "blue", disabled: false, visible: false, label: "monthly reduction targets", per: "river basin"},
        {color: "blue", disabled: false, visible: false, label: "monthly blue water footprints and caps", per: "river basin"},
        {color: "grey", disabled: true,  visible: true,  label: "water pollution level (nitrogen)", per: "river basin"},
        {color: "grey", disabled: false, visible: false, label: "water pollution level (phosphorus)", per: "river basin"}
      ]
    };
  },
  computed: {
    viewColor() {
      return this.assessment.viewColor;
    },
    viewColorCap() {
      return this.viewColor[0].toUpperCase() + this.viewColor.slice(1);
    },
    scenario() {
      return this.assessment.scenario;
    },
    hasCsv() {
      return (this.scenario != null) && (this.scenario.csvHeaders != null) && (this.scenario.csvHeaders != "");
    },
    parseConfig() {
      if (this.hasCsv) {
        return {
          delimiter: this.scenario.csvDelimiter,
          quoteChar: this.scenario.csvQuoteChar
        };
      }
      return {};
    },
    unparseConfig() {
      if (this.hasCsv) {
        return {
          delimiter: this.parseConfig.delimiter || ",",
          quoteChar: this.parseConfig.quoteChar || '"'
        };
      }
      return {};
    },
    columns() {
      if (this.scenario == null) {
        return [];
      }

      let columns = [];

      if (this.hasCsv) {
        const headers = Papa.parse(this.scenario.csvHeaders, this.parseConfig).data[0];
        columns = columns.concat(headers.map((header, i) => {
          return {key: "csv", label: header, lookupFunction: csv => Papa.parse(csv, this.parseConfig).data[0][i]};
        }));
      } else {
        columns = columns.concat([
          {key: "name",        label: "Location"},
          {key: "abstraction", label: "Abstraction (m³/yr)", dataFunction: val => getWFString(Math.round(val), "yr")},
          {key: "effluent",    label: "Effluent (m³/yr)",    dataFunction: val => getWFString(Math.round(val), "yr")},
          {key: "loss",        label: "Loss (m³/yr)",        dataFunction: val => getWFString(Math.round(val), "yr")}
        ]);
      }

      columns = columns.concat([
        {key: this.viewColor + "WF", label: this.viewColorCap + " WF (m³/yr)", dataFunction: val => getWFString(Math.round(val), "yr")}
      ]);

      if (this.scenario.quantity > 0) {
        const unit = quantityUnits.find(unit => unit.unitId == this.scenario.unitID);
        columns = columns.concat([
          {key: this.viewColor + "WFx", label: this.viewColorCap + " WF (m³/" + unit.short + ")", dataFunction: getFunctionGetWFString(unit.short)}
        ]);
      }

      columns = columns.concat([
        {key: "hs5BasinId", label: "Basin ID (Hydrosheds 05)", dataFunction: val => val || "[no basin]", classes: ["group"]}
      ]);

      if (this.viewColor == "blue") {
        columns = columns.concat([
          {key: "blueReds", label: "Months with Reduction Targets",        lookupFunction: blueReds => blueReds?.split(/,/)[15], dataFunction: val => val ? (parseInt(val)) : "n/a", classes: ["group"]},
          {key: "blueReds", label: "Annual Average Reduction Target (%)",  lookupFunction: blueReds => blueReds?.split(/,/)[12], dataFunction: val => val ? (val + "%") : "n/a",     classes: ["group"]},
          {key: "blueReds", label: "Reduction in Most Critical Month (%)", lookupFunction: blueReds => blueReds?.split(/,/)[14], dataFunction: val => val ? (val + "%") : "n/a",     classes: ["group"]}
        ]);

        if (this.columnGroups[2].visible) {
          columns.push({key: "whiteSpace", label: "", classes: ["white-space"]});
          columns = columns.concat(months.slice(0, 12).map((month, i) => {
            return {key: "blueReds", label: "Reduction Target " + month.short + " (%)", lookupFunction: blueReds => blueReds?.split(/,/)[i], dataFunction: val => val ? (val + "%") : "n/a", classes: ["group"]};
          }));
        }

        if (this.columnGroups[3].visible) {
          columns.push({key: "whiteSpace", label: "", classes: ["white-space"]});
          columns = columns.concat(months.slice(0, 12).map((month, i) => {
            return {key: "blueWfs", label: "Blue WF " + month.short + " (m³/mon)", lookupFunction: blueWfs => blueWfs?.split(/,/)[i] || "", dataFunction: getFunctionGetWFString("mon"), classes: ["group"]};
          }));
          columns.push({key: "whiteSpace", label: "", classes: ["white-space"]});
          columns = columns.concat(months.slice(0, 12).map((month, i) => {
            return {key: "blueCaps", label: "Blue WF Cap " + month.short + " (m³/mon)", lookupFunction: blueCaps => blueCaps?.split(/,/)[i] || "", dataFunction: getFunctionGetWFString("mon"), classes: ["group"]};
          }));
        }
      }

      if (this.viewColor == "grey") {
        columns = columns.concat([
          {key: "greyWsN", label: "Water Pollution Level (Nitrogen)", dataFunction: getWSString, classes: ["group"]}
        ]);

        if (this.columnGroups[5].visible) {
          columns = columns.concat([
            {key: "greyWsP", label: "Water Pollution Level (Phosphorus)", dataFunction: getWSString, classes: ["group"]}
          ]);
        }
      }

      columns.forEach((column, i) => {
        if (column.classes && column.classes.includes("group")) {
          if ((i == 0) || !columns[i - 1].classes || !columns[i - 1].classes.includes("group")) {
            column.classes.push("left-row");
          }
          if ((i == columns.length - 1) || !columns[i + 1].classes || !columns[i + 1].classes.includes("group")) {
            column.classes.push("right-row");
          }
        }
      });

      return columns;
    },
    rows() {
      return this.scenarioProcesses;
    },
    loading() {
      return (scenariosLoader.status.loading || scenarioProcessesLoader.status.loading);
    }
  }
}
</script>
